import type { UnwrapNestedRefs } from "vue";
import isEqual from "lodash-es/isEqual";
import isArray from "lodash-es/isArray";
import cloneDeep from "lodash-es/cloneDeep";
import { h, nextTick, onBeforeMount, reactive, watch } from "vue";
import type { ComponentOptionsBase, ComponentPublicInstance, DefineComponent, ExtractPropTypes, Prop, PropType, SetupContext, Slots, VNode, VNodeArrayChildren } from "vue";
import type { FetchDataFilterType, FindInfoOperateType, IsSuccessType, UrlFunctionType } from "../../commonMix/findInfoData/types";
import findInfoData, { findInfoProps } from "../../commonMix/findInfoData";
import type { FindInfoPropsType } from "../../commonMix/findInfoData";
import { isFunction } from "lodash-es";
export interface SetChildrenComponentRenderFunc {
  (d: unknown): {
    props: { [key: string]: any };
    children?:
      | string
      | number
      | boolean
      | VNode
      | VNodeArrayChildren
      | (() => any)
      | {
          [name: string]: unknown;
          $stable?: boolean;
        };
  };
}
export type OptionType = {
  label: string;
  disabled?: boolean;
  value: string | number;
};
export interface ResOptionsDataFilterType {
  (d: any): OptionType[];
}
export type ComponentPropsType = {
  [key: string]: Prop<any, any>;
};
export type GetOptionsComponentState = {
  options: OptionType[];
  value: string | number | boolean | any[];
};
export type SetKeysType = {
  label: string;
  value: string;
};
export interface GetOptionsComponentType extends FindInfoOperateType {
  state: UnwrapNestedRefs<GetOptionsComponentState>;
  optionsRender(): VNode | VNode[];
}
export default function (
  component: DefineComponent,
  componentProps: ComponentPropsType,
  childrenComponent: DefineComponent,
  setChildrenComponentRenderFunc?: SetChildrenComponentRenderFunc
): { component: any; props: ComponentPropsType } {
  const MegreProps = {
    ...findInfoProps,
    ...componentProps,
    getFirstOption: {
      //获取首个请求到的值作为默认选中值
      type: Boolean,
      default: false,
    },
    options: {
      //选项配置
      type: Array as PropType<Array<OptionType>>,
      default: () => [],
    },
    setKeys: {
      //选项配置
      type: Object as PropType<SetKeysType>,
      default: () => ({
        label: "label",
        value: "value",
      }),
    },
    modelValue: {
      type: [String, Number, Boolean, Array],
      default: "",
    },
    /**
     * 配置返回的数据过滤方法
     */
    resDataFilter: {
      type: Function as PropType<ResOptionsDataFilterType>,
      default: (res: unknown) => res,
    },
  };
  type MegrePropsType = ExtractPropTypes<typeof MegreProps> & FindInfoPropsType;
  const setConf: any = {
    props: MegreProps,

    setup(
      props: MegrePropsType,
      ctx: SetupContext<any>
    ): GetOptionsComponentType {
      const state = reactive({
        options: cloneDeep(props.options) || [],
        value: cloneDeep(props.modelValue) || "",
      });
      watch(
        () => props.options,
        (newValue: OptionType[]) => {
          state.options = newValue;
        }
      );
      watch(
        () => props.modelValue,
        (newValue: any) => {
          if (!isEqual(state.value, newValue)) {
            state.value = newValue;
          }
        }
      );
      const { loading, findInfoState, stateFetchData, loadData } = findInfoData(
        props,
        ctx,
        (d: any): void => {
          console.log(d);
          state.options = d;
          if (
            props.getFirstOption &&
            (!state.value || !(state.value as any[]).length)
          ) {
            state.value = d[0].value;
            nextTick(() => {
              ctx.emit("afterSetFirstOptionValue", this.setValue);
            });
          }
          ctx.emit("success", d);
        },
        (e: Error): void => {
          console.log(e);
          ctx.emit("error", e);
          state.options = [];
        }
      );

      function optionsRender(): VNode | VNode[] {
        if (!childrenComponent) return [];
        return state.options.map((item, idx) => {
          let setAttr = Object.assign(
            {},
            {
              key: item[props.setKeys.value],
              label: item[props.setKeys.label],
              disabled: item.disabled,
              value: item[props.setKeys.value],
            }
          );

          let child = null;
          if (isFunction(setChildrenComponentRenderFunc)) {
            const res = setChildrenComponentRenderFunc(setAttr);
            setAttr = Object.assign({}, setAttr, res.props);
            child = res.children;
          }

          return h(childrenComponent, setAttr, child ? child : undefined);
        });
      }

      return {
        loading,
        findInfoState,
        stateFetchData,
        loadData,
        state,

        optionsRender,
      };
    },

    render(
      _ctx: ComponentPublicInstance<MegrePropsType, GetOptionsComponentType>
    ): VNode {
      const { state } = _ctx;
      const slots: Slots = _ctx.$slots;

      const setProps = Object.assign({}, _ctx.$props);

      return h(
        component,
        {
          "onUpdate:modelValue": (v: any) => {
            state.value = v;

            _ctx.$emit("update:modelValue", v);
          },

          ...setProps,
          ...Object.assign({}, _ctx.$attrs, { options: state.options }),
          slots: slots && Object.keys(slots).length ? slots : undefined,
          modelValue: state.value,
        },
        !slots || !Object.keys(slots).length
          ? { default: _ctx.optionsRender }
          : undefined
      );
    },
  };
  return {
    component: setConf,
    props: MegreProps,
  };
}
